import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { ToastService } from '../../../core/services/toast.service';

@Component({
  selector: 'app-toast',
  imports: [TranslatePipe],
  template: `
    @if (toast(); as message) {
      <div
        class="toast"
        [class.toast--error]="message.type === 'error'"
        [class.toast--warning]="message.type === 'warning'"
        [attr.role]="message.type === 'error' ? 'alert' : 'status'"
        aria-live="polite"
      >
        <span class="toast__message" [innerHTML]="message.messageKey | translate"></span>
        @if (message.actionLabelKey) {
          <button class="toast__action" type="button" (click)="onAction()">
            <span [innerHTML]="message.actionLabelKey | translate"></span>
          </button>
        }
      </div>
    }
  `,
  styles: `
    .toast {
      position: fixed;
      bottom: 1.5rem;
      left: 50%;
      z-index: 2000;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      max-width: min(90vw, 40rem);
      padding: 0.875rem 1.25rem;
      transform: translateX(-50%);
      background: var(--color-primary);
      box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.25);
      color: var(--color-text-strong);
      font-size: 0.875rem;
    }

    .toast--error {
      background: var(--color-error);
      color: var(--color-text-inverse);
    }

    .toast__message {
      min-width: 0;
      font-size: 0.875rem;
      font-weight: 700;
    }

    .toast__action {
      flex: 0 0 auto;
      padding: 0.375rem 1rem;
      border: 2px solid var(--color-gray-400);
      border-radius: 0;
      background: transparent;
      color: var(--color-gray-400);
      font-size: 0.875rem;
      font-weight: 700;
      text-transform: uppercase;
      transition:
        background-color 0.15s ease-in-out,
        color 0.15s ease-in-out;

      &:hover {
        background: var(--color-gray-400);
        color: var(--color-white);
      }

      &:focus-visible {
        outline: 2px solid var(--color-gray-400);
        outline-offset: 2px;
      }
    }

    .toast--error .toast__action {
      border-color: var(--color-white);
      color: var(--color-white);

      &:hover {
        background: var(--color-white);
        color: var(--color-error);
      }

      &:focus-visible {
        outline-color: var(--color-white);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Toast {
  private readonly toastService = inject(ToastService);

  protected readonly toast = this.toastService.currentToast;

  protected onAction(): void {
    this.toastService.dismiss();
  }
}
